import { debounce } from 'lodash'
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'

import {
  LOCATION_PICKER_INPUT_DEBOUNCE,
  LOCATION_PICKER_LOADING_THRESHOLD,
  LOCATION_PICKER_MIN_QUERY_LENGTH
} from 'src/constants/location_picker'
import {
  PlaceSearchError,
  PlaceSuggestionType,
  characterCount,
  fetchPlaceSuggestions
} from 'src/utils/places/autocomplete'
import { initialPlaceSearchState, placeSearchReducer } from 'src/utils/places/place_search_reducer'
import { reportError } from 'src/utils/reporting/report-errors'

interface IParams {
  area?: string;
  type?: PlaceSuggestionType;
}

export const usePlaceSuggestions = ({ area, type }: IParams) => {
  const [state, dispatch] = useReducer(placeSearchReducer, initialPlaceSearchState)

  const paramsRef = useRef<IParams>({ area, type })
  paramsRef.current = { area, type }
  const stateRef = useRef(state)
  stateRef.current = state

  const lastIdRef = useRef(0)
  // the text the field holds, as far as the search has seen it typed
  const fieldQueryRef = useRef('')
  const controllerRef = useRef<AbortController | null>(null)
  const slowTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const stopRunningRequest = useCallback(() => {
    controllerRef.current?.abort()
    controllerRef.current = null
    clearTimeout(slowTimerRef.current)
  }, [])

  const run = useCallback(async (query: string) => {
    stopRunningRequest()
    const id = ++lastIdRef.current
    const controller = new AbortController()
    controllerRef.current = controller

    dispatch({ id, type: 'started' })
    slowTimerRef.current = setTimeout(() => dispatch({ id, type: 'slow' }), LOCATION_PICKER_LOADING_THRESHOLD)

    try {
      const suggestions = await fetchPlaceSuggestions({ ...paramsRef.current, query, signal: controller.signal })
      dispatch({ id, suggestions, type: 'succeeded' })
    } catch (error) {
      if (controller.signal.aborted) {
        return
      }
      const kind = error instanceof PlaceSearchError ? error.kind : 'failed'
      if (kind === 'failed') {
        reportError('Place search failed', { status: error instanceof PlaceSearchError ? error.status : undefined })
      }
      dispatch({ error: kind, id, type: 'failed' })
    } finally {
      if (id === lastIdRef.current) {
        clearTimeout(slowTimerRef.current)
      }
    }
  }, [stopRunningRequest])

  const debouncedRun = useMemo(() => debounce(run, LOCATION_PICKER_INPUT_DEBOUNCE), [run])

  const reset = useCallback(() => {
    debouncedRun.cancel()
    stopRunningRequest()
    fieldQueryRef.current = ''
    dispatch({ id: ++lastIdRef.current, type: 'reset' })
  }, [debouncedRun, stopRunningRequest])

  const search = useCallback((query: string) => {
    if (characterCount(query) < LOCATION_PICKER_MIN_QUERY_LENGTH) {
      reset()
      return
    }
    fieldQueryRef.current = query
    debouncedRun(query)
  }, [debouncedRun, reset])

  const cancel = useCallback(() => {
    debouncedRun.cancel()
    stopRunningRequest()
  }, [debouncedRun, stopRunningRequest])

  const searchNow = useCallback(() => {
    // after a failure the text in the field is searched, not the one that failed
    if (stateRef.current.status === 'error' && fieldQueryRef.current) {
      debouncedRun.cancel()
      run(fieldQueryRef.current)
      return
    }
    debouncedRun.flush()
  }, [debouncedRun, run])

  useEffect(() => {
    const retryWhenBackOnline = () => {
      const { status, error } = stateRef.current
      if (status === 'error' && error === 'offline' && fieldQueryRef.current) {
        run(fieldQueryRef.current)
      }
    }
    window.addEventListener('online', retryWhenBackOnline)
    return () => window.removeEventListener('online', retryWhenBackOnline)
  }, [run])

  useEffect(() => () => {
    debouncedRun.cancel()
    stopRunningRequest()
  }, [debouncedRun, stopRunningRequest])

  return { cancel, reset, search, searchNow, state }
}
